import { Dialog } from "@headlessui/react";
import React, { useMemo, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import Loading from "../../../common-components/Loader";
import SelectInput from "../../../common-components/CustomSelect";
import { OptionType } from "../../TMM/commonInterfaceTypes";
import { Control, UseFormWatch } from "react-hook-form";
import type { PrintHistoryRow } from "../modules/InventorySalesContainer";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import {
  formatCurrencyOne,
  formatCurrency,
} from "../../../common-components/commonFunctions";

interface PrintHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: PrintHistoryRow[];
  allRows: PrintHistoryRow[];
  isLoading: boolean;
  error: string | null;
  isbnDisplay?: string | null;
  imageSrc?: string;
  control: Control<any>;
  watch: UseFormWatch<any>;
  isbnNumberOptions: OptionType[];
  isbnTitleOptions: OptionType[];
  changeIsbnInfo: (event: any) => void;
  isFirefox: boolean;
  hasMoreIsbn: boolean;
}

type SortKey = keyof PrintHistoryRow;

interface SortConfig {
  key: SortKey;
  direction: "asc" | "desc";
}

const PrintHistoryModal: React.FC<PrintHistoryModalProps> = ({
  isOpen,
  onClose,
  rows,
  allRows,
  isLoading,
  error,
  isbnDisplay,
  imageSrc,
  control,
  watch,
  isbnNumberOptions,
  isbnTitleOptions,
  changeIsbnInfo,
  isFirefox,
  hasMoreIsbn,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: "PRINTING",
    direction: "desc",
  });

  const handleClose = () => {
    onClose();
  };

  const formatUnits = (
    value: string | number | null | undefined
  ): string => {
    const numericValue = Number(value ?? 0);
    if (isNaN(numericValue)) return "-";
    return formatCurrencyOne(numericValue, 0, "");
  };

  const formatPrice = (
    value: string | number | null | undefined
  ): string => {
    const numericValue = Number(value ?? 0);
    if (isNaN(numericValue)) return "-";
    return formatCurrency(numericValue, 2);
  };

  const getDateString = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value.value) return String(value.value);
    return null;
  };

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortRows = (inputRows: PrintHistoryRow[]): PrintHistoryRow[] => {
    if (!sortConfig) return inputRows;

    const { key, direction } = sortConfig;

    const sorted = [...inputRows].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      // Date sorting for WHSE_DUE_DATE
      if (key === "WHSE_DUE_DATE") {
        const aDateStr = getDateString(aVal);
        const bDateStr = getDateString(bVal);
        const aDate = aDateStr ? moment(aDateStr) : null;
        const bDate = bDateStr ? moment(bDateStr) : null;
        if (!aDate && !bDate) return 0;
        if (!aDate) return -1;
        if (!bDate) return 1;
        return aDate.valueOf() - bDate.valueOf();
      }

      // Numeric sorting for PRINTING, PRINT_QTY, TOTAL_RECEIVED_QTY
      const aNum = aVal !== undefined && aVal !== null ? Number(aVal) : NaN;
      const bNum = bVal !== undefined && bVal !== null ? Number(bVal) : NaN;

      const aIsNum = !isNaN(aNum);
      const bIsNum = !isNaN(bNum);

      if (aIsNum && bIsNum) {
        return aNum - bNum;
      }

      // Fallback to string comparison
      const aStr = aVal !== undefined && aVal !== null ? String(aVal) : "";
      const bStr = bVal !== undefined && bVal !== null ? String(bVal) : "";
      return aStr.localeCompare(bStr);
    });

    if (direction === "desc") {
      sorted.reverse();
    }

    return sorted;
  };

  const sortedRows = useMemo(
    () => sortRows(rows),
    [rows, sortConfig]
  );

  // Use allRows (including latest print) for export, but keep UI table using filtered rows
  const sortedAllRows = useMemo(
    () => sortRows(allRows),
    [allRows, sortConfig]
  );

  const renderSortIndicator = (key: SortKey) => {
    const isActive = sortConfig?.key === key;

    if (!isActive || !sortConfig) {
      return <FaSort className="text-xs" />;
    }

    if (sortConfig.direction === "asc") {
      // Reverse icon appearance: show down icon when sorting ascending
      return <FaSortDown className="text-xs" />;
    }

    // Reverse icon appearance: show up icon when sorting descending
    return <FaSortUp className="text-xs" />;
  };

  const handleExport = () => {
    if (!sortedAllRows || sortedAllRows.length === 0) return;

    // For Excel, export raw numeric values so that SUM and other formulas work correctly.
    const toNumber = (value: any): number => {
      const num = Number(value ?? 0);
      return Number.isNaN(num) ? 0 : num;
    };

    // Export numeric values (no currency symbols) so Excel can sum them,
    // and then apply US-style comma formatting via Excel number formats.
    const rowsForExport = sortedAllRows.map((row) => ({
      "Printing Cycle": row.PRINTING ?? "",
      "WHSE Date": getDateString(row.WHSE_DUE_DATE)
        ? moment(getDateString(row.WHSE_DUE_DATE)).format("MM/DD/YYYY")
        : "",
      "Print QTY": toNumber(row.PRINT_QTY),
      "Total Received QTY": toNumber(row.TOTAL_RECEIVED_QTY),
      "USA Price": toNumber(row.US_PRICE),
      "Canadian Price": toNumber(row.CANADIAN_PRICE),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsForExport);

    // Apply number formats with comma separation:
    // - "Print QTY" and "Total Received QTY": #,##0
    // - "US Price" and "Canadian Price": #,##0.00
    const range = worksheet["!ref"] ? XLSX.utils.decode_range(worksheet["!ref"]) : null;
    if (range) {
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const printQtyRef = XLSX.utils.encode_cell({ r: R, c: 2 }); // "Print QTY"
        const totalReceivedRef = XLSX.utils.encode_cell({ r: R, c: 3 }); // "Total Received QTY"
        const usPriceRef = XLSX.utils.encode_cell({ r: R, c: 4 }); // "US Price"
        const canadianPriceRef = XLSX.utils.encode_cell({ r: R, c: 5 }); // "Canadian Price"

        const printQtyCell = worksheet[printQtyRef];
        if (printQtyCell && typeof printQtyCell.v === "number") {
          printQtyCell.z = "#,##0";
        }

        const totalReceivedCell = worksheet[totalReceivedRef];
        if (totalReceivedCell && typeof totalReceivedCell.v === "number") {
          totalReceivedCell.z = "#,##0";
        }

        const usPriceCell = worksheet[usPriceRef];
        if (usPriceCell && typeof usPriceCell.v === "number") {
          usPriceCell.z = "#,##0.00";
        }

        const canadianPriceCell = worksheet[canadianPriceRef];
        if (canadianPriceCell && typeof canadianPriceCell.v === "number") {
          canadianPriceCell.z = "#,##0.00";
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Print History");

    const safeIsbn = isbnDisplay
      ? String(isbnDisplay).replace(/[^0-9A-Za-z]/g, "")
      : "print_history";
    const fileName = `${safeIsbn}_print_history.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Dialog open={isOpen} onClose={() => {}}>
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div
          className="absolute inset-0 bg-black opacity-40"
          onClick={handleClose}
        ></div>

        <div className="relative bg-white rounded-lg shadow-2xl w-[95vw] max-w-7xl mx-auto max-h-[95vh] flex flex-col">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-600"
            title="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>

          {/* Top bar with cover, ISBN and Title selectors */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="py-2 flex gap-4 items-center">
              <div className="flex items-center">
                <div className="w-[56px] h-[80px] border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-white">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="Book cover"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-2">
                      No Cover
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-4">
                <div className="flex gap-4">
                  <div className="w-[30%]">
                    <p className="text-[#e31c23] font-bold">ISBN</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="isbn"
                        options={isbnNumberOptions}
                        placeholder="Select ISBN"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <p className="text-[#e31c23] font-bold">BOOK TITLE</p>
                    <div className="w-full grid grid-cols-1">
                      <SelectInput
                        control={control}
                        name="title"
                        options={isbnTitleOptions}
                        placeholder="Select Book Title"
                        onChangeCallback={changeIsbnInfo}
                        watch={watch}
                        isFirefox={isFirefox}
                        hasMoreIsbn={hasMoreIsbn}
                        copyBtn={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 text-white font-semibold text-lg flex justify-between items-center">
            <span>Print History</span>
            <div className="flex items-center gap-4">
              {isbnDisplay && (
                <span className="text-sm font-normal">
                  ISBN: {isbnDisplay}
                </span>
              )}
              {sortedRows.length > 0 && (
                <button
                  type="button"
                  onClick={handleExport}
                  className="px-4 py-1.5 text-sm font-semibold bg-white text-[#e31c23] border border-[#e31c23] rounded shadow hover:bg-gray-100"
                >
                  Export
                </button>
              )}
            </div>
          </div>

          <Dialog.Panel className="px-6 py-4 overflow-auto flex-1 flex flex-col">
            <Dialog.Description className="flex-1 flex flex-col">
              <Loading isLoading={isLoading}>
                {error ? (
                  <div className="w-full flex flex-col justify-center items-center py-8">
                    <p className="text-red-600 font-semibold mb-2">
                      Failed to load print history
                    </p>
                    <p className="text-gray-500 text-sm text-center">{error}</p>
                  </div>
                ) : rows.length === 0 ? (
                  <div className="w-full flex flex-col justify-center items-center py-8">
                    <p className="text-gray-600 font-semibold text-lg mb-2">
                      No print history available
                    </p>
                    <p className="text-gray-500 text-sm text-center">
                      There are no historical print records to display for this
                      ISBN (latest printing is excluded).
                    </p>
                  </div>
                ) : (
                  <div className="w-full overflow-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center gap-1">
                              <span>Printing Cycle</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("PRINTING")}
                              >
                                {renderSortIndicator("PRINTING")}
                              </button>
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center gap-1">
                              <span>WHSE Date</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("WHSE_DUE_DATE")}
                              >
                                {renderSortIndicator("WHSE_DUE_DATE")}
                              </button>
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-right font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>Print QTY</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("PRINT_QTY")}
                              >
                                {renderSortIndicator("PRINT_QTY")}
                              </button>
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-right font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>Total Received QTY</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("TOTAL_RECEIVED_QTY")}
                              >
                                {renderSortIndicator("TOTAL_RECEIVED_QTY")}
                              </button>
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-right font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>USA Price</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("US_PRICE")}
                              >
                                {renderSortIndicator("US_PRICE")}
                              </button>
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-right font-semibold text-gray-700 select-none"
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>Canadian Price</span>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
                                onClick={() => handleSort("CANADIAN_PRICE")}
                              >
                                {renderSortIndicator("CANADIAN_PRICE")}
                              </button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedRows.map((row, index) => (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                              {row.PRINTING !== undefined && row.PRINTING !== null
                                ? formatUnits(row.PRINTING)
                                : "N/A"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                              {getDateString(row.WHSE_DUE_DATE)
                                ? moment(
                                    getDateString(row.WHSE_DUE_DATE)
                                  ).format("MM/DD/YYYY")
                                : "N/A"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                              {formatUnits(row.PRINT_QTY)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                              {formatUnits(row.TOTAL_RECEIVED_QTY)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                              {formatPrice(row.US_PRICE)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                              {formatPrice(row.CANADIAN_PRICE)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Loading>
            </Dialog.Description>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default PrintHistoryModal;

